import { Module } from 'vuex';
import { StorageMgr, storageMgr } from '../../../common/storageMgr';
import type { AppState } from '../types';
import logger from '../../../common/utils/logger';
import { loginManager } from '../../../common/auth/loginManager';

export type UserInfo = {
  token: string;
  userGuid?: string;
  mobile?: string;
  key?: string;
  ticket?: string;
  thirdId?: string; // 三方授权ID类似openid
}

export type UserState = {
  userInfo: UserInfo
  boComAuthStatus: 'loading' | 'success' | 'failed' | null /** 交银小程序授权状态 */
}

type StateModule = Module<UserState, AppState>

// initial state
const initState: StateModule['state'] = {
  userInfo: {
    token: '',
    mobile: '',
    userGuid: '',
  },
  boComAuthStatus: null,
};

// getters
const getters: StateModule['getters'] = {
  // userToken: (state: UserState) => state.userToken,
};

// actions
const actions: StateModule['actions'] = {
  actionRequestUserInfo: async (
    { commit },
    options?: {
      forceRefresh?: boolean;
    },
  ) => {
    const loginResult = await loginManager.signIn({
      forceRefresh: options?.forceRefresh,
    });

    logger('actionRequestUserInfo', loginResult);
    if (loginResult.success && loginResult.userInfo) {
      commit('setUserInfo', loginResult.userInfo);
      return loginResult.userInfo;
    }

    // 登录失败时，抛出错误
    const error = new Error(loginResult.error || '登录失败');
    (error as Error & { loginResult: typeof loginResult }).loginResult = loginResult;
    throw error;
  },

  // 新增：强制重新登录
  actionRelogin: async ({ commit }) => {
    const loginResult = await loginManager.relogin();

    if (loginResult.success && loginResult.userInfo) {
      commit('setUserInfo', loginResult.userInfo);
      return loginResult.userInfo;
    }

    const error = new Error(loginResult.error || '重新登录失败');
    (error as Error & { loginResult: typeof loginResult }).loginResult = loginResult;
    throw error;
  },

  // 新增：登出
  actionLogout: async ({ commit }) => {
    await loginManager.signOut();

    // 清空 store 中的用户信息
    commit('setUserInfo', {
      token: '',
      mobile: '',
      userGuid: '',
    });
  },

  // 新增：检查登录状态
  actionCheckLoginStatus: async () => loginManager.checkLoginStatus(),
};

// mutations
const mutations: StateModule['mutations'] = {
  setUserState: (state, userState: UserState) => {
    if (!userState) {
      return;
    }

    const keys = Object.keys(userState) as (keyof UserState)[];
    for (let i = 0; i < keys.length; i += 1) {
      state[keys[i]] = userState[keys[i]];
    }
  },
  setUserInfo: (state, userInfo: UserInfo & { guid?: string }) => {
    if (!userInfo) {
      return;
    }

    const {
      guid,
      userGuid,
      ...rest
    } = userInfo;
    const resolvedGuid = userGuid || guid;
    const resolvedUserInfo = {
      userGuid: resolvedGuid,
      ...rest,
    };
    state.userInfo = resolvedUserInfo;
    if (resolvedUserInfo && resolvedUserInfo.token) {
      storageMgr.setItem(StorageMgr.USERINFO, resolvedUserInfo);
    }
  },
  setBoComAuthStatus: (state, status: 'loading' | 'success' | 'failed' | null) => {
    state.boComAuthStatus = status;
  },
};

export default {
  namespaced: true,
  state: initState,
  getters,
  actions,
  mutations,
};
